/**
 * Screen wake lock, PLAN.md §7 step 6.
 *
 * A phone that sleeps mid-lecture suspends `MediaRecorder`. The lock is
 * released by the browser whenever the tab is hidden, so it has to be taken
 * again on every `visibilitychange` - that re-acquire is the whole point of
 * this file. Unsupported browsers (Firefox, older Safari) simply get nothing;
 * recording still works, the screen just sleeps.
 */

type WakeLockSentinelLike = { released: boolean; release: () => Promise<void> };
type WakeLockLike = { request: (type: "screen") => Promise<WakeLockSentinelLike> };

function wakeLockApi(): WakeLockLike | null {
  const candidate = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  return candidate ?? null;
}

export class ScreenWakeLock {
  private sentinel: WakeLockSentinelLike | null = null;
  private active = false;
  private readonly onVisibility = () => {
    if (this.active && document.visibilityState === "visible") {
      void this.acquire();
    }
  };

  async enable(): Promise<void> {
    if (this.active) return;
    this.active = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    await this.acquire();
  }

  async disable(): Promise<void> {
    this.active = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    const sentinel = this.sentinel;
    this.sentinel = null;
    if (sentinel !== null && !sentinel.released) {
      await sentinel.release().catch(() => undefined);
    }
  }

  private async acquire(): Promise<void> {
    const api = wakeLockApi();
    if (api === null) return;
    if (this.sentinel !== null && !this.sentinel.released) return;
    try {
      this.sentinel = await api.request("screen");
    } catch {
      // Denied (battery saver, unsupported) - not worth surfacing.
      this.sentinel = null;
    }
  }
}
